/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ErrorCode } from '../modules/lxs-create-order/lxs-create-order.data';
import { trackedAction } from '../utilities/track-execution-time';

/**
 * GetCartOrderNumber Input Action
 */
export class GetCartOrderNumberInput implements Msdyn365.IActionInput {
    public orderNumber: string;
    constructor(orderNumber: string) {
        this.orderNumber = orderNumber;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `GetCartOrderNumber-${this.orderNumber}`;
    public getCacheObjectType = () => 'GetCartOrderNumber';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetCartOrderNumberData {
    '@odata.context'?: string;
    error?: ErrorCode;
    AdditionalInfo: string;
    SalesOrg: string;
    SalesOrgText: string;
    DistributionChannel: string;
    DistributionChannelText: string;
    Division: string;
    DivisionText: string;
    Incoterm1: string;
    Incoterm2: string;
    SoldToId: string;
    ShipToId: string;
    SoldTo: ISoldToTableData;
    ShipTo: IShipToTableData;
    Items: IReorderDataItem[];
}

export interface IReorderDataItem {
    Number: number;
    ProductNumber: string;
    ProductName: string;
    CustomerProductNumber: string;
    RequestedDate: string;
    Quantity: number;
    UnitOfMeasure: string;
    EndCustomerNumber: string;
    EndCustomerName: string;
}

export interface ISoldToTableData {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
}

export interface IShipToTableData {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
}
const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetCartOrderNumberInput('');
};

async function action(input: GetCartOrderNumberInput, ctx: Msdyn365.IActionContext): Promise<IGetCartOrderNumberData> {
    const response = await Msdyn365.sendRequest<IGetCartOrderNumberData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetReorderData(language='${ctx.requestContext.locale}',orderNumber='${input.orderNumber}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );

    if (response.status === 200 && response.data) {
        return { ...response.data };
    } else {
        return { ...({} as IGetCartOrderNumberData), error: { errorCode: response.status, errorText: response.statusText } };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetCartOrderNumberData>>((input, ctx) => trackedAction(action, input, ctx, 'GetReorderData')),
    id: 'GetCartOrderNumber',
    input: createInput
});
